'use client';

import { Button } from '@heroui/react';
import { useAtom } from '@reatom/npm-react';
import { useBack } from '@trwl/hooks/useBack';
import { Container } from '@trwl/layout';
import { Path } from '@trwl/shared/lib/router';
import {
  FavouriteIcon,
  Logo,
  Search as SearchIcon,
} from '@trwl/shared/ui/icons';
import { $navLinks, cartAtom, likesAtom, loadBrands } from '@trwl/store';
import { AppBanner } from '@trwl/widgets/nav/header/components/AppBanner';
import { InfoRow } from '@trwl/widgets/nav/header/components/InfoRow';
import { SearchSidebar } from '@trwl/widgets/nav/search-sidebar';
import { NavigationSidebar } from '@trwl/widgets/nav/sidebar';
import cn from 'classnames';
import { useUnit } from 'effector-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { AiOutlineSearch, AiOutlineShopping } from 'react-icons/ai';
import { BiMenu } from 'react-icons/bi';
import { HiOutlineUserCircle } from 'react-icons/hi2';
import { IoChevronBackOutline } from 'react-icons/io5';

export const Header = () => {
  const back = useBack();
  const sidebarActive = useUnit(NavigationSidebar.store.$state);
  const sidebarHandler = useUnit(NavigationSidebar.store.stateChanged);
  const searchActive = useUnit(SearchSidebar.store.$state);
  const searchHandler = useUnit(SearchSidebar.store.stateChanged);
  const pathname = usePathname();

  const isProduct = pathname.startsWith(Path.PRODUCT);

  const links = useUnit($navLinks);
  const [cart] = useAtom(cartAtom);
  const [likes] = useAtom(likesAtom);

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    loadBrands();
  }, [links]);

  const cartItems = useMemo(
    () => cart.reduce((acc, cur) => acc + cur.count, 0),
    [cart],
  );

  return (
    <>
      <AppBanner />
      <InfoRow />
      <header
        className={cn(
          'w-full bg-[#fff] z-50 sticky top-0',
          isScrolled && 'border-b-[#F6F6F6] border-b-[2px] box-border',
        )}
      >
        <Container className={'pt-1 pb-0.5'}>
          <div
            className={
              'flex flex-row md:flex-col gap-4 xl:gap-1 my-1.5 xl:my-4 justify-between'
            }
          >
            <div className={'md:hidden'}>
              {isProduct ? (
                <IoChevronBackOutline
                  style={{
                    width: 25,
                    height: 25,
                  }}
                  onClick={() => {
                    back(Path.CATALOG);
                  }}
                  className="cursor-pointer"
                />
              ) : (
                <AiOutlineSearch
                  style={{
                    width: 25,
                    height: 25,
                  }}
                  onClick={() =>
                    searchHandler({
                      active: !searchActive.active,
                    })
                  }
                  className="cursor-pointer"
                />
              )}
            </div>
            <div className="flex flex-row justify-center">
              <Link href={Path.HOME}>
                <Logo className={'h-[24px] md:h-[30px] lg:h-[32px]'} />
              </Link>
            </div>
            <div className={'md:hidden'}>
              <Link href={Path.CART}>
                <div className="relative">
                  {cartItems > 0 && (
                    <div className="absolute flex justify-center text-[10px] items-center top-[-5px] right-[-5px] w-[16px] h-[16px] pt-[1px] bg-red-500 rounded-[9999px] text-white">
                      {cartItems > 9 ? '9+' : cartItems}
                    </div>
                  )}
                  <AiOutlineShopping className="w-[26px] h-[26px]" />
                </div>
              </Link>
            </div>

            <div
              className={
                'hidden md:flex flex-row items-center justify-between gap-4'
              }
            >
              <div className="hidden lg:flex flex-row w-full justify-start items-center gap-10 ">
                {links.map((route) => (
                  <Link
                    key={route.id}
                    className="whitespace-nowrap text-[16px] text-[#282828]"
                    href={route.attributes?.link ?? '#'}
                    target={
                      route.attributes?.link?.includes('https://')
                        ? '_blank'
                        : undefined
                    }
                  >
                    {route.attributes?.title}
                  </Link>
                ))}
              </div>

              <div className="flex flex-row row md:w-full justify-between lg:justify-end items-center gap-6">
                <div
                  className="!h-[42px] max-w-[320px] w-full md:block hidden relative"
                  onClick={(event) => {
                    event.preventDefault();
                    searchHandler({ active: true });
                  }}
                >
                  <Button
                    startContent={
                      <SearchIcon className="h-[22px] min-w-[22px] cursor-pointer" />
                    }
                    onPress={() => searchHandler({ active: true })}
                    className={
                      '!rounded-[12px] bg-[#F0F0F0] text-[#aaa7b0] text-[17px] max-w-[320px] w-full justify-start'
                    }
                  >
                    Поиск
                  </Button>
                </div>
                <div
                  className={
                    'hidden md:flex flex-row gap-6 justify-end items-center'
                  }
                >
                  <Link href={Path.FAVORITES}>
                    <div className="relative">
                      {likes.length > 0 && (
                        <div className="absolute flex justify-center text-[10px] items-center top-[-5px] right-[-7px] w-[16px] h-[16px] pt-[1px] bg-red-500 rounded-[9999px] text-white">
                          {likes.length > 9 ? '9+' : likes.length}
                        </div>
                      )}
                      <FavouriteIcon className="w-[23px] h-[23px]" />
                    </div>
                  </Link>
                  <Link href={Path.CART}>
                    <div className="relative">
                      {cartItems > 0 && (
                        <div className="absolute flex justify-center text-[10px] items-center top-[-5px] right-[-7px] w-[16px] h-[16px] pt-[1px] bg-red-500 rounded-[9999px] text-white">
                          {cartItems > 9 ? '9+' : cartItems}
                        </div>
                      )}
                      <AiOutlineShopping className="w-[26px] h-[26px]" />
                    </div>
                  </Link>
                  <Link href={Path.PROFILE} className="relative">
                    <HiOutlineUserCircle className="w-[26px] h-[26px]" />
                  </Link>
                  <BiMenu
                    className="cursor-pointer w-[26px] h-[26px] md:flex lg:hidden hidden"
                    onClick={() =>
                      sidebarHandler({
                        active: !sidebarActive.active,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </Container>
      </header>
    </>
  );
};
